import { useState } from "react";
import { format, parseISO, isValid } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

/**
 * DateField — Sudefootball-branded date picker.
 * Drop-in replacement for `<input type="date">`.
 *
 * Props:
 *   value: ISO string "YYYY-MM-DD" (or "")
 *   onChange: (isoString) => void
 *   placeholder, disabled, minDate, maxDate, testid, className
 *
 * Renders as a monospace pill with a calendar icon. Opens a popover with the
 * shadcn calendar. Values are always emitted as ISO YYYY-MM-DD to keep
 * backend contracts identical to native date inputs.
 */
export default function DateField({
  value,
  onChange,
  placeholder = "Selecciona fecha",
  disabled,
  minDate,
  maxDate,
  testid,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const parsed = value ? (() => { try { const d = parseISO(value); return isValid(d) ? d : null; } catch { return null; } })() : null;
  const label = parsed ? format(parsed, "d MMM yyyy", { locale: es }) : placeholder;
  const yearNow = new Date().getFullYear();
  const fromYear = minDate ? new Date(minDate).getFullYear() : 1950;
  const toYear = maxDate ? new Date(maxDate).getFullYear() : yearNow + 2;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-testid={testid}
          disabled={disabled}
          className={`w-full h-10 bg-transparent border border-border px-3 font-mono text-sm text-left flex items-center gap-2 hover:border-primary/60 focus:border-primary focus:outline-none disabled:opacity-50 ${className}`}
        >
          <CalendarIcon size={14} className="text-primary shrink-0" />
          <span className={parsed ? "" : "text-muted-foreground"}>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 border border-border bg-card" align="start">
        <Calendar
          mode="single"
          locale={es}
          captionLayout="dropdown-buttons"
          fromYear={fromYear}
          toYear={toYear}
          defaultMonth={parsed || undefined}
          classNames={{
            caption_dropdowns: "flex gap-1",
            caption_label: "hidden",
            dropdown: "h-8 bg-card border border-border px-1 text-sm font-mono focus:border-primary focus:outline-none cursor-pointer",
            dropdown_month: "",
            dropdown_year: "",
            vhidden: "hidden",
          }}
          selected={parsed || undefined}
          onSelect={(d) => {
            if (!d) return;
            onChange(format(d, "yyyy-MM-dd"));
            setOpen(false);
          }}
          disabled={(d) => {
            if (minDate && d < new Date(minDate)) return true;
            if (maxDate && d > new Date(maxDate)) return true;
            return false;
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
